import React from "react";
import { Link } from "react-router-dom";
import appIcon from "../assets/tiny-jpg-png/icon-128.png";
import forestBear from "../assets/tiny-jpg-png/web/forest-bear-960.webp";
import forestBearSmall from "../assets/tiny-jpg-png/web/forest-bear-640.webp";
import bearBefore from "../assets/tiny-jpg-png/web/bear-before.webp";
import bearAfter from "../assets/tiny-jpg-png/web/bear-after.webp";
import bearBento from "../assets/tiny-jpg-png/web/bear-bento.webp";
import batchResults from "../assets/tiny-jpg-png/web/batch-results.webp";
import TinyJpgPngStoreButton from "../components/TinyJpgPngStoreButton";
import styles from "../InverluxApps.module.css";
import tiny from "./TinyJpgPng.module.css";

const comparison = [
  ["Maximum single image size", "20 MB", "150 MB"],
  ["Images per batch", "30", "No fixed count limit*"],
  ["JPG compression", "Automatic", "Adjustable quality"],
  ["PNG compression", "Automatic lossy", "Lossless & lossy options"],
  ["Individual & ZIP downloads", "Included", "Included"],
  ["Image processing", "Local", "Local"],
  ["Ads", "None", "None"],
  ["Pricing", "Free", "$14.99 USD one-time"],
];

const questions = [
  {
    question: "Do my images leave my computer?",
    answer:
      "No. JPG and PNG compression runs locally in Chrome. Your images, filenames, pixels, and image metadata are not uploaded to Inverlux or an image-processing server.",
  },
  {
    question: "What can I do with Free?",
    answer:
      "Compress up to 30 images per batch, with a maximum of 20 MB per image. Drop files into the extension, see the savings, compare the results, and download images individually or together as a ZIP.",
  },
  {
    question: "Is compression lossless?",
    answer:
      "Free uses automatic lossy JPG and PNG compression. Pro adds adjustable JPG quality and a choice of lossless or lossy PNG compression. Preview your results before downloading; your original files stay untouched.",
  },
  {
    question: "How much smaller will my images become?",
    answer:
      "Savings depend on the image and compression settings. Some images have more room for optimization than others. The extension shows the measured original size, result size, and savings for each file.",
  },
  {
    question: "Can I use it offline?",
    answer:
      "Free image compression works offline after the extension is installed. Pro purchase, activation, and periodic license verification require an internet connection. Image processing always stays local.",
  },
  {
    question: "Is there a subscription?",
    answer:
      "No. Free stays free, and Pro is a one-time purchase with no recurring subscription. Both Free and Pro compress images locally on your device.",
  },
  {
    question: "How do I upgrade to Pro?",
    answer:
      "Open the extension and choose Explore Pro for purchase instructions. Pro costs $14.99 USD one-time, with no subscription and permanent Pro access for 3 active Chrome installations. A 30-day refund-request window applies from the date of purchase.",
  },
  {
    question: "Are there any practical batch limits?",
    answer:
      "Pro has no fixed image-count limit, but all batches depend on available browser memory and processing resources. Large files or ZIP downloads may require smaller batches. MB means 1,000,000 bytes.",
  },
];

const TinyJpgPng = () => (
  <div className={tiny.page}>
    <section className={tiny.hero} id="install">
      <div className={styles.contentWidth}>
        <div className={tiny.heroGrid}>
          <div>
            <div className={tiny.identity}>
              <img src={appIcon} alt="" width="56" height="56" />
              <div>
                <p className={tiny.productName}>Tiny JPG &amp; PNG</p>
                <p className={tiny.publisher}>By Inverlux LLC</p>
              </div>
            </div>
            <p className={tiny.eyebrow}>Private image compression for Chrome</p>
            <h1 className={tiny.heroTitle}>
              No uploads.{" "}<br />
              <span>No subscriptions.</span>
            </h1>
            <p className={tiny.heroSubtitle}>
              <strong className={tiny.localPromise}>JPG &amp; PNG compression. All local.</strong>
              Your images stay on your device in both Free and Pro. Start
              free, or upgrade with a single payment.
            </p>
            <div className={styles.heroActions}>
              <TinyJpgPngStoreButton />
              <a className={tiny.textLink} href="#plans">Compare Free &amp; Pro →</a>
            </div>
            <ul className={tiny.trustList} aria-label="Product highlights">
              <li>Images stay on your device</li>
              <li>Free or one-time Pro</li>
              <li>No ads</li>
            </ul>
          </div>
          <figure className={tiny.heroFigure}>
            <img
              className={tiny.heroImage}
              src={forestBear}
              srcSet={forestBearSmall + " 640w, " + forestBear + " 960w"}
              sizes="(max-width: 900px) calc(100vw - 40px), 520px"
              alt="A friendly brown bear holding a salmon beside a sunny forest river."
              width="960"
              height="540"
              decoding="async"
            />
            <figcaption>
              Smaller images. Bigger smiles.
            </figcaption>
          </figure>
        </div>
        <div className={tiny.highlights}>
          <div><strong>100% local</strong><span>image compression</span></div>
          <div><strong>30 images</strong><span>per Free batch</span></div>
          <div><strong>20 MB</strong><span>per image on Free</span></div>
        </div>
      </div>
    </section>

    <section className={styles.section} aria-labelledby="tiny-benefits">
      <div className={styles.contentWidth}>
        <div className={tiny.storyGrid}>
          <div className={styles.sectionHeading}>
            <p className={tiny.eyebrow}>Smaller images, one bite at a time</p>
            <h2 id="tiny-benefits">A lighter file. A simpler workflow.</h2>
            <p>
              Just as our bear makes a meal of a salmon, Tiny JPG &amp; PNG
              takes a bite out of image file sizes. Drop in your files and
              let the extension do the work, right on your computer.
            </p>
            <p>
              Batch compression, clear savings statistics, and a clean
              workspace help you prepare images for websites, email, and
              everyday sharing.
            </p>
          </div>
          <div className={tiny.mascotStory} aria-label="The bear’s salmon shrinks as compression progresses">
            <figure>
              <img src={bearBefore} alt="The bear holding a full-sized salmon." width="280" height="280" loading="lazy" />
              <figcaption>Ready for a bite</figcaption>
            </figure>
            <span className={tiny.storyArrow} aria-hidden="true">→</span>
            <figure>
              <img src={bearAfter} alt="The happy bear holding a smaller salmon and giving a thumbs-up." width="280" height="280" loading="lazy" />
              <figcaption>A lighter finish</figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>

    <section className={styles.section + " " + tiny.softSection} aria-labelledby="tiny-workflow">
      <div className={styles.contentWidth}>
        <div className={styles.sectionHeading}>
          <p className={tiny.eyebrow}>Three steps. All on your device.</p>
          <h2 id="tiny-workflow">Drop. Compress. Download.</h2>
        </div>
        <div className={styles.stepsGrid}>
          <article className={styles.stepCard}>
            <span className={styles.stepNumber}>01</span>
            <h3>Drop images</h3>
            <p>Drag in JPG and PNG files, or choose them from your computer. Free handles up to 30 images per batch.</p>
          </article>
          <article className={styles.stepCard}>
            <span className={styles.stepNumber}>02</span>
            <h3>Compress locally</h3>
            <p>Compress on your device, with no uploads in Free or Pro. See the original size, optimized size, and measured savings for each image.</p>
          </article>
          <article className={styles.stepCard}>
            <span className={styles.stepNumber}>03</span>
            <h3>Download</h3>
            <p>Preview the result, then save images individually or download the batch as a ZIP. Your originals stay untouched.</p>
          </article>
        </div>
        <figure className={styles.demoMedia + " " + tiny.workspace}>
          <a href={batchResults} target="_blank" rel="noopener noreferrer" aria-label="View the full-size Tiny JPG & PNG batch results screenshot">
            <img
              className={styles.productScreenshot}
              src={batchResults}
              alt="The Tiny JPG & PNG extension showing three completed images, individual savings, preview controls, and Download all as ZIP."
              width="1280"
              height="800"
              loading="lazy"
            />
          </a>
          <figcaption>
            Actual extension results from a sample batch. Savings vary by image
            and compression settings. Open the image to view it at full size.
          </figcaption>
        </figure>
      </div>
    </section>

    <section className={styles.section + " " + tiny.anchorSection} id="plans" aria-labelledby="tiny-plans">
      <div className={styles.contentWidth}>
        <div className={styles.sectionHeading}>
          <p className={tiny.eyebrow}>Start Free. Make room for more.</p>
          <h2 id="tiny-plans">Everyday batches or a bigger appetite?</h2>
          <p>No subscriptions on either tier. Image compression stays local in Free and Pro.</p>
        </div>
        <div className={tiny.comparisonWrap} role="region" aria-label="Free and Pro feature comparison" tabIndex={0}>
          <table className={tiny.comparison}>
            <caption>Tiny JPG &amp; PNG Free and Pro features</caption>
            <thead>
              <tr><th scope="col">Feature</th><th scope="col">Free</th><th scope="col">Pro</th></tr>
            </thead>
            <tbody>
              {comparison.map(([feature, free, pro]) => (
                <tr key={feature}>
                  <th scope="row">{feature}</th><td>{free}</td><td>{pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={tiny.planNote}>
          *All batches remain subject to browser memory, processing, and ZIP
          limits.
        </p>

        <div className={tiny.proPanel}>
          <img
            className={tiny.bentoImage}
            src={bearBento}
            alt="The happy brown bear with a salmon bento, representing the optional Pro upgrade."
            width="640"
            height="480"
            loading="lazy"
          />
          <div>
            <p className={tiny.eyebrow}>Meet Tiny JPG &amp; PNG Pro</p>
            <h2>More room.<br />One happy bear.</h2>
            <p>
              Work with images up to 150 MB each, batches with no fixed image-count
              limit, adjustable JPG quality, and lossless or lossy PNG options.
            </p>
            <p className={tiny.proPrice}>$14.99 USD one-time.</p>
            <p>No subscription. Permanent Pro access for 3 active Chrome installations.</p>
            <p>
              30-day refund-request window from the date of purchase. Contact{" "}
              <a href="mailto:support@inverlux.com">support@inverlux.com</a> for refund requests.
            </p>
            <p className={tiny.licenseNote}>
              Purchases and upgrades are handled through the extension.
              Pro activation and periodic license verification need an
              internet connection; your images always stay local.
            </p>
            <a className={tiny.textLink} href="#install">Get the extension first →</a>
          </div>
        </div>
      </div>
    </section>

    <section className={styles.section + " " + tiny.softSection} id="privacy" aria-labelledby="tiny-privacy">
      <div className={styles.narrowWidth}>
        <p className={tiny.eyebrow}>Your images are your business</p>
        <h2 id="tiny-privacy">All compression. All local.</h2>
        <p className={tiny.privacyIntro}>
          Image compression happens entirely on your device in both Free and
          Pro. There are no image uploads, no ads, and no analytics. Free
          requires no account, and your original files are never overwritten.
        </p>
        <p className={tiny.licenseNote}>
          Optional Pro payment and licensing are configured. Purchase,
          activation, and periodic license verification require internet
          access. Licensing runs on AWS in Oregon (us-west-2), separate from
          local image processing. The extension is available on the
          Chrome Web Store.
        </p>
        <div className={styles.inlineLinks}>
          <Link to="/tiny-jpg-png/privacy">Read the Tiny JPG &amp; PNG Privacy Policy →</Link>
        </div>
      </div>
    </section>

    <section className={styles.section} aria-labelledby="tiny-faq">
      <div className={styles.narrowWidth}>
        <p className={tiny.eyebrow}>A few things you might be wondering</p>
        <h2 id="tiny-faq">Frequently asked questions.</h2>
        <div className={styles.faqList}>
          {questions.map(({ question, answer }) => (
            <details key={question}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <section className={tiny.finalCta}>
      <div className={styles.narrowWidth}>
        <p className={tiny.eyebrow}>Tiny JPG &amp; PNG, by Inverlux LLC</p>
        <h2>Ready for lighter images?</h2>
        <p>All-local image compression. Free or one-time Pro. Available now on the Chrome Web Store.</p>
        <div className={tiny.finalActions}>
          <TinyJpgPngStoreButton />
          <Link className={tiny.textLink} to="/support">Questions? Get support →</Link>
        </div>
      </div>
    </section>
  </div>
);

export default TinyJpgPng;
